/**
 * WorkspaceContext - Global workspace state management
 *
 * Uses Redis-cached backend APIs for workspace selection.
 * Provides:
 * - Active workspace state
 * - Workspace switching
 * - Auto-selection for single workspace users
 * - X-Workspace-ID header injection
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../services/api/client';
import { auth } from '../api/client';

// Create context
const WorkspaceContext = createContext(null);

/**
 * Check if user has a valid (non-expired) token
 * Returns false if no token or token is expired
 */
const hasValidToken = () => {
  const token = localStorage.getItem('access_token');
  if (!token) return false;

  // Check if token is expired by decoding the JWT payload
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp;
    if (exp && Date.now() >= exp * 1000) {
      // Token is expired, remove it and return false
      localStorage.removeItem('access_token');
      return false;
    }
    return true;
  } catch {
    // If token parsing fails, assume it's invalid
    return false;
  }
};

/**
 * Check if current path is a public route that doesn't require auth
 * Matches the publicPaths in API client interceptor
 */
const isPublicPath = () => {
  const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/magic-login', '/share', '/sign', '/verify-email', '/setup', '/accept-invitation', '/invitation', '/auth-redirect', '/dedicated-server', '/security/verify-login'];
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  return publicPaths.some(path => currentPath.startsWith(path));
};

/**
 * WorkspaceProvider component
 */
export const WorkspaceProvider = ({ children }) => {
  // State
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSelector, setShowSelector] = useState(false);

  /**
   * Fetch active workspace from backend (Redis cache)
   */
  const fetchActiveWorkspace = useCallback(async () => {
    // Skip API call if no valid token - prevents 401 errors during auth transitions
    if (!hasValidToken()) return null;

    try {
      const response = await apiClient.get('/workspace/active');
      if (response.data.is_selected && response.data.workspace_id) {
        setActiveWorkspace({
          id: response.data.workspace_id,
          name: response.data.workspace_name,
          company_name: response.data.company_name,
          city: response.data.city,
          scans_count: response.data.scans_count,
          logo_base64: response.data.logo_base64,
          vpn_config_id: response.data.vpn_config_id,
          vpn_type: response.data.vpn_type,
          vpn_name: response.data.vpn_name,
        });
        // Set header for future requests
        apiClient.defaults.headers.common['X-Workspace-ID'] = response.data.workspace_id;
        return response.data;
      }
      return null;
    } catch (err) {
      // Silently handle 401 - user will be redirected to login by API client
      if (err.response?.status !== 401) {
        console.error('Failed to fetch active workspace:', err);
      }
      return null;
    }
  }, []);

  /**
   * Fetch user's available workspaces
   */
  const fetchWorkspaces = useCallback(async (search = '') => {
    // Skip API call if no valid token - prevents 401 errors during auth transitions
    if (!hasValidToken()) {
      setWorkspaces([]);
      return [];
    }

    try {
      const params = search ? { search } : {};
      const response = await apiClient.get('/workspace/list', { params });
      setWorkspaces(response.data || []);
      return response.data || [];
    } catch (err) {
      // Silently handle 401 - user will be redirected to login by API client
      if (err.response?.status !== 401) {
        console.error('Failed to fetch workspaces:', err);
      }
      setWorkspaces([]);
      return [];
    }
  }, []);

  /**
   * Switch to a different workspace
   */
  const switchWorkspace = useCallback(async (workspaceId) => {
    // Skip API call if no valid token - prevents 401 errors during auth transitions
    if (!hasValidToken()) return false;

    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post('/workspace/switch', {
        workspace_id: workspaceId,
      });

      if (response.data.success) {
        // Find workspace details from list
        const ws = workspaces.find(w => w.id === workspaceId) || {
          id: response.data.workspace_id,
          name: response.data.workspace_name,
        };

        setActiveWorkspace({
          id: response.data.workspace_id,
          name: response.data.workspace_name,
          ...ws,
        });

        // Update header
        apiClient.defaults.headers.common['X-Workspace-ID'] = response.data.workspace_id;

        setShowSelector(false);
        return true;
      }
      return false;
    } catch (err) {
      // Silently handle 401 - user will be redirected to login by API client
      if (err.response?.status !== 401) {
        console.error('Failed to switch workspace:', err);
        setError(err.response?.data?.detail || 'Failed to switch workspace');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [workspaces]);

  /**
   * Clear active workspace
   */
  const clearWorkspace = useCallback(async () => {
    // Skip API call if no valid token - prevents 401 errors during auth transitions
    if (!hasValidToken()) {
      setActiveWorkspace(null);
      delete apiClient.defaults.headers.common['X-Workspace-ID'];
      return true;
    }

    try {
      await apiClient.delete('/workspace/active');
      setActiveWorkspace(null);
      delete apiClient.defaults.headers.common['X-Workspace-ID'];
      return true;
    } catch (err) {
      // Silently handle 401 - user will be redirected to login by API client
      if (err.response?.status !== 401) {
        console.error('Failed to clear workspace:', err);
      }
      return false;
    }
  }, []);

  /**
   * Try to auto-select workspace (for single workspace users)
   */
  const tryAutoSelect = useCallback(async () => {
    // Skip API call if no valid token - prevents 401 errors during auth transitions
    if (!hasValidToken()) return { autoSelected: false, reason: 'no_token' };

    try {
      const response = await apiClient.post('/workspace/auto-select');

      if (response.data.auto_selected && response.data.workspace_id) {
        setActiveWorkspace({
          id: response.data.workspace_id,
          name: response.data.workspace_name,
        });
        apiClient.defaults.headers.common['X-Workspace-ID'] = response.data.workspace_id;
        return { autoSelected: true, workspace: response.data };
      }

      return {
        autoSelected: false,
        reason: response.data.reason,
        workspacesCount: response.data.workspaces_count,
      };
    } catch (err) {
      // Silently handle 401 - user will be redirected to login by API client
      if (err.response?.status !== 401) {
        console.error('Auto-select failed:', err);
      }
      return { autoSelected: false, reason: 'error' };
    }
  }, []);

  /**
   * Initialize workspace context
   */
  const initialize = useCallback(async () => {
    // Skip initialization on public paths - no workspace needed there
    // This prevents 401 errors when WorkspaceProvider is mounted on public routes
    if (isPublicPath()) {
      setLoading(false);
      return;
    }

    // Check for valid (non-expired) token before making API calls
    // This prevents 401 errors during auth transitions or with expired tokens
    if (!hasValidToken()) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First, try to get active workspace from Redis
      const active = await fetchActiveWorkspace();

      if (active && active.is_selected) {
        // Already have active workspace
        await fetchWorkspaces();
        setLoading(false);
        return;
      }

      // No active workspace, try auto-select
      const result = await tryAutoSelect();

      if (result.autoSelected) {
        await fetchWorkspaces();
        setLoading(false);
        return;
      }

      // Need manual selection
      await fetchWorkspaces();

      if (result.reason === 'no_workspaces') {
        // No workspaces - user needs to create one
        setLoading(false);
        return;
      }

      if (result.reason === 'multiple_workspaces') {
        // Multiple workspaces - show selector
        setShowSelector(true);
      }

      setLoading(false);
    } catch (err) {
      // Silently handle 401 - user will be redirected to login by API client
      if (err.response?.status !== 401) {
        console.error('Workspace initialization failed:', err);
        setError(err.message);
      }
      setLoading(false);
    }
  }, [fetchActiveWorkspace, fetchWorkspaces, tryAutoSelect]);

  /**
   * Refresh workspace data
   */
  const refresh = useCallback(async () => {
    // Skip API calls if no valid token - prevents 401 errors during auth transitions
    if (!hasValidToken()) return;

    await fetchActiveWorkspace();
    await fetchWorkspaces();
  }, [fetchActiveWorkspace, fetchWorkspaces]);

  // Initialize on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Re-initialize when auth changes
  useEffect(() => {
    const handleAuthChange = () => {
      // Skip on public paths
      if (isPublicPath()) return;

      // Use hasValidToken to also check for expired tokens
      if (hasValidToken()) {
        initialize();
      } else {
        setActiveWorkspace(null);
        setWorkspaces([]);
        delete apiClient.defaults.headers.common['X-Workspace-ID'];
      }
    };

    // Listen for storage events (login/logout from other tabs)
    window.addEventListener('storage', handleAuthChange);
    return () => window.removeEventListener('storage', handleAuthChange);
  }, [initialize]);

  // Context value
  const value = {
    // State
    activeWorkspace,
    workspaces,
    loading,
    error,
    showSelector,

    // Computed
    hasWorkspace: !!activeWorkspace?.id,
    workspaceId: activeWorkspace?.id,
    workspaceName: activeWorkspace?.name,

    // Actions
    switchWorkspace,
    clearWorkspace,
    refresh,
    fetchWorkspaces,
    setShowSelector,
    tryAutoSelect,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

/**
 * Hook to access workspace context
 */
export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

/**
 * Hook for workspace-required pages
 * Redirects to workspace selector if no workspace is selected
 */
export const useRequireWorkspace = () => {
  const context = useWorkspace();

  useEffect(() => {
    if (!context.loading && !context.hasWorkspace && context.workspaces.length > 0) {
      context.setShowSelector(true);
    }
  }, [context.loading, context.hasWorkspace, context.workspaces.length, context.setShowSelector]);

  return context;
};

export default WorkspaceContext;
